import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';

import { Card } from '@/components/ui/card';
import { IconWell } from '@/components/ui/icon-well';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';

export interface StatCardProps {
  label: string;
  value?: ReactNode;
  icon?: ReactNode;
  comparison?: {
    change: number;
    label: string;
    increaseIsGood?: boolean;
    formatted: string;
  };
  loading?: boolean;
  hint?: string;
  className?: string;
}

export const StatCard = ({
  label,
  value,
  icon,
  comparison,
  loading = false,
  hint,
  className,
}: StatCardProps) => {
  const direction =
    comparison === undefined
      ? 'flat'
      : comparison.change > 0
        ? 'up'
        : comparison.change < 0
          ? 'down'
          : 'flat';

  const good =
    direction === 'flat'
      ? null
      : (comparison?.increaseIsGood ?? true) === (direction === 'up');

  return (
    <Card className={cn('p-4', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {hint ? (
            <Tooltip content={hint}>
              <p className="cursor-help text-body-sm text-fg-secondary underline decoration-dotted underline-offset-2">
                {label}
              </p>
            </Tooltip>
          ) : (
            <p className="text-body-sm text-fg-secondary">{label}</p>
          )}

          <div className="mt-1.5 flex min-h-[1.75rem] items-center">
            {loading ? (
              <Skeleton className="h-7 w-28" />
            ) : (
              <div className="text-section-title font-semibold tabular-nums text-fg">{value}</div>
            )}
          </div>
        </div>

        {icon ? <IconWell>{icon}</IconWell> : null}
      </div>

      {comparison && !loading ? (
        <p className="mt-3 flex items-center gap-1.5 text-caption">
          <span
            className={cn(
              'inline-flex items-center gap-0.5 font-medium',
              good === null ? 'text-fg-secondary' : good ? 'text-success-fg' : 'text-danger-fg',
            )}
          >
            {direction === 'up' ? (
              <TrendingUp aria-hidden className="h-3.5 w-3.5" />
            ) : direction === 'down' ? (
              <TrendingDown aria-hidden className="h-3.5 w-3.5" />
            ) : (
              <Minus aria-hidden className="h-3.5 w-3.5" />
            )}
            {comparison.formatted}
          </span>
          <span className="text-fg-tertiary">{comparison.label}</span>
        </p>
      ) : null}
    </Card>
  );
};
